import React, { useRef, useState } from 'react';
import { CalendarPlus, CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

export const MeetingRequestButton = ({ p, t, onSent }) => {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const [manualCopy, setManualCopy] = useState(false);
  const pending = useRef(false);
  const request = sent || p.meeting_request;
  const hasSent = Boolean(request?.sent_at);
  const scheduled = p.meeting?.start_at && new Date(p.meeting.start_at).getTime() > Date.now();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((p.email || '').trim());
  const key = (s) => `prospects.meetingRequest.${s}`;
  const send = async () => {
    if (pending.current || hasSent) return;
    pending.current = true; setBusy(true); setError('');
    try {
      const { data } = await api.post(`/admin/prospects/${p.id}/meeting-request`);
      setSent(data);
      toast.success(t(key(data.already_sent ? 'alreadySent' : 'success')));
      onSent();
    } catch (e) {
      const message = e?.response?.data?.detail || t(key('failed'));
      setError(message); toast.error(message);
    } finally { pending.current = false; setBusy(false); }
  };
  const copy = async () => {
    setError(''); setManualCopy(false);
    try { await navigator.clipboard.writeText(request.url); toast.success(t(key('copied'))); }
    catch (_) { setManualCopy(true); }
  };
  if (p.status !== 'in_talks' || (scheduled && !hasSent)) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 min-w-0" data-testid={`meeting-request-${p.id}`}>
      <Button size="sm" variant="outline" onClick={send} disabled={busy || hasSent || !validEmail}
        className="border-blue-200 text-blue-800 bg-blue-50 hover:bg-blue-100 max-w-full h-auto min-h-9 whitespace-normal text-left"
        data-testid={`meeting-request-send-${p.id}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" /> : hasSent ? <CheckCircle2 className="h-4 w-4 mr-2 shrink-0" /> : <CalendarPlus className="h-4 w-4 mr-2 shrink-0" />}
        {t(key(busy ? 'sending' : hasSent ? 'sent' : 'send'))}
      </Button>
      {hasSent && <>
        <span className="text-xs text-emerald-800 break-all" data-testid={`meeting-request-status-${p.id}`}>
          {t(key('status'), { email: request.to, when: new Date(request.sent_at).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) })}
        </span>
        <Button size="sm" variant="ghost" onClick={copy} title={t(key('copy'))} aria-label={t(key('copy'))} data-testid={`meeting-request-copy-${p.id}`}><Copy className="h-4 w-4" /></Button>
      </>}
      {manualCopy && <div className="basis-full min-w-0 space-y-1" data-testid={`meeting-request-copy-fallback-${p.id}`}>
        <p role="status" className="text-xs text-slate-600" data-testid={`meeting-request-copy-hint-${p.id}`}>{t(key('copyManually'))}</p>
        <input readOnly autoFocus value={request.url} onFocus={(e) => e.target.select()} onClick={(e) => e.target.select()}
          aria-label={t(key('copy'))} className="w-full min-w-0 rounded-md border border-blue-200 px-2 py-2 text-xs"
          data-testid={`meeting-request-copy-input-${p.id}`} />
      </div>}
      {!validEmail && !hasSent && <p className="text-xs text-amber-800 basis-full" data-testid={`meeting-request-no-email-${p.id}`}>{t(key('noEmail'))}</p>}
      {error && <p role="alert" className="text-xs text-rose-700 basis-full break-words" data-testid={`meeting-request-error-${p.id}`}>{error}</p>}
    </div>
  );
};